import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, Plus, Trash2 } from "lucide-react";
import {
  getMembers,
  inviteMember,
  removeMember,
  TeamMember,
} from "@/services/team-service";
import LoadingSpinner from "./LoadingSpinner";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface TeamModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TeamModal = ({ isOpen, onClose }: TeamModalProps) => {
  const [newEmail, setNewEmail] = useState("");
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const currentUser = useSelector((state: RootState) => state.auth.user);

  const handleInvite = async () => {
    try {
      const members = await inviteMember({
        email: newEmail,
        name: "Member",
      });
      setMembers(members);
    } catch (error) {
      toast.error("Failed to intive member");
    } finally {
      toast.success(`Invitation sent to ${newEmail}`);
      setNewEmail("");
    }
  };

  const handleRemoveMember = async (id: string, username: string) => {
    try {
      const members = await removeMember(id);
      setMembers(members);
    } catch (error) {
      toast.error("Failed to remove member");
    } finally {
      toast.success(`Removed ${username} from team`);
      setNewEmail("");
    }
  };

  const loadMembers = async () => {
    try {
      const members = await getMembers();
      setMembers(members);
    } catch (error) {
      toast.error("Failed to load members");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMembers();
  }, []);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Team Management</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="flex gap-2">
            <Input
              placeholder="Enter email address"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="flex-1"
            />
            <Button onClick={handleInvite} disabled={!newEmail}>
              <Plus className="mr-2 h-4 w-4" />
              Invite
            </Button>
          </div>

          <div className="space-y-4">
            {members.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between p-4 rounded-lg border bg-card"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center">
                    <Mail className="h-5 w-5 text-secondary-foreground" />
                  </div>
                  <div>
                    <p className="font-medium">{member.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {member.username}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground">
                    {member.role}
                  </span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              handleRemoveMember(member.id, member.username)
                            }
                            disabled={
                              currentUser?.role !== "ADMIN" ||
                              member.role === "ADMIN"
                            }
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Only admins can remove members</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TeamModal;
